import { baseApi } from '../baseApi';
import type {
  AccessPolicyDocument,
  ComponentGrant,
  MyAccess,
} from '@lib/accessPolicy';

/**
 * RTK Query endpoints for the Component Access Control feature
 * (design.md §Components, Decision 1/2/5). All endpoints are practice-scoped
 * (or system-admin scoped for the template) on the `api-onboarding` surface.
 *
 * Cache tags:
 *   - `AccessControl`        — the per-practice Access_Policy document.
 *   - `MyAccess`             — the caller's resolved effective access.
 *   - `DefaultPolicyTemplate`— the system-wide default policy template.
 *
 * A successful Access_Policy save invalidates both `AccessControl` (so the
 * matrix re-reads the persisted document) and `MyAccess` (so card/route/nav
 * gating re-evaluates), per Req 11.5 / Req 8.3.
 *
 * Requirements: 8.3, 11.5, 18.8
 */

/** Body for a full-document Access_Policy save (Decision 1, Req 11.5). */
export interface UpdateAccessPolicyRequest {
  clinicId: string;
  /** Full per-component grant map keyed by `componentId`. */
  components: Record<string, ComponentGrant>;
}

/** Body for a full-document System_Default_Policy_Template save (Req 18.6/18.8). */
export interface UpdateDefaultPolicyTemplateRequest {
  /** Full per-component grant map keyed by `componentId`. */
  components: Record<string, ComponentGrant>;
}

const accessControlApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // --- Per-practice Access_Policy (Practice_Owner) ---
    getAccessPolicy: builder.query<AccessPolicyDocument, string>({
      query: (clinicId) => ({
        method: 'GET' as const,
        path: `/onboarding/practice/${encodeURIComponent(clinicId)}/access-policy`,
      }),
      transformResponse: (r: { policy: AccessPolicyDocument } | AccessPolicyDocument) =>
        (r && typeof r === 'object' && 'policy' in r ? r.policy : r) as AccessPolicyDocument,
      providesTags: ['AccessControl'],
    }),
    updateAccessPolicy: builder.mutation<AccessPolicyDocument, UpdateAccessPolicyRequest>({
      query: ({ clinicId, components }) => ({
        method: 'PUT' as const,
        path: `/onboarding/practice/${encodeURIComponent(clinicId)}/access-policy`,
        body: { components },
      }),
      transformResponse: (r: { policy: AccessPolicyDocument } | AccessPolicyDocument) =>
        (r && typeof r === 'object' && 'policy' in r ? r.policy : r) as AccessPolicyDocument,
      invalidatesTags: ['AccessControl', 'MyAccess'],
    }),

    // --- My_Access_Resolver (any Active_Member) ---
    getMyAccess: builder.query<MyAccess, string>({
      query: (clinicId) => ({
        method: 'GET' as const,
        path: `/onboarding/practice/${encodeURIComponent(clinicId)}/my-access`,
      }),
      transformResponse: (r: { myAccess: MyAccess } | MyAccess) =>
        (r && typeof r === 'object' && 'myAccess' in r ? r.myAccess : r) as MyAccess,
      providesTags: ['MyAccess'],
    }),

    // --- System_Default_Policy_Template (Medrender_Owner, /admin) ---
    getDefaultPolicyTemplate: builder.query<AccessPolicyDocument, void>({
      query: () => ({
        method: 'GET' as const,
        path: '/onboarding/admin/access-policy-template',
      }),
      transformResponse: (r: { template: AccessPolicyDocument } | AccessPolicyDocument) =>
        (r && typeof r === 'object' && 'template' in r ? r.template : r) as AccessPolicyDocument,
      providesTags: ['DefaultPolicyTemplate'],
    }),
    updateDefaultPolicyTemplate: builder.mutation<
      AccessPolicyDocument,
      UpdateDefaultPolicyTemplateRequest
    >({
      query: ({ components }) => ({
        method: 'PUT' as const,
        path: '/onboarding/admin/access-policy-template',
        body: { components },
      }),
      transformResponse: (r: { template: AccessPolicyDocument } | AccessPolicyDocument) =>
        (r && typeof r === 'object' && 'template' in r ? r.template : r) as AccessPolicyDocument,
      invalidatesTags: ['DefaultPolicyTemplate'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAccessPolicyQuery,
  useUpdateAccessPolicyMutation,
  useGetMyAccessQuery,
  useGetDefaultPolicyTemplateQuery,
  useUpdateDefaultPolicyTemplateMutation,
} = accessControlApi;

export { accessControlApi };
